import React from 'react';
import { Modal } from '../../ui/Modal/Modal';
import { Button } from '../../ui/Button/Button';
import { Field } from './Field';

export interface TypedConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  /** The exact text the technician has to type back, e.g. the RCU serial. */
  expected: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Confirmation for commands that erase, reboot or re-credential the device.
 *
 * A tap is too cheap for something that erases every mapping, and the device
 * gives no undo, so the technician types the serial number back. The dialog
 * spells out what is lost and what happens next.
 */
export const TypedConfirmDialog: React.FC<TypedConfirmDialogProps> = ({
  isOpen,
  title,
  message,
  expected,
  confirmLabel,
  onConfirm,
  onCancel,
}) => {
  const [typed, setTyped] = React.useState('');
  const [wasOpen, setWasOpen] = React.useState(isOpen);

  /**
   * Each opening starts clean, so a previous confirmation cannot carry over.
   * Adjusting state during render is React's own pattern for a prop change.
   */
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    setTyped('');
  }

  const matches = typed.trim() === expected;

  return (
    <Modal isOpen={isOpen} onClose={onCancel} title={title} size="sm">
      <p className="text-sm text-[#666666]">{message}</p>
      <p className="mt-3 rounded-xl bg-[#F5F5F5] px-3 py-2 text-sm text-[#333333]">
        Type <span className="font-mono font-medium">{expected}</span> to confirm.
      </p>
      <div className="mt-4">
        <Field
          label="Confirmation"
          value={typed}
          onChange={setTyped}
          placeholder={expected}
          autoFocus
        />
      </div>
      <div className="mt-6 flex items-center justify-end gap-3">
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="primary"
          disabled={!matches}
          onClick={onConfirm}
          className={matches ? 'bg-[#F44336] shadow-none hover:bg-[#D32F2F]' : ''}
        >
          {confirmLabel}
        </Button>
      </div>
    </Modal>
  );
};

export default TypedConfirmDialog;
